import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import './theme.css';
import './responsive.css';
import type {
  AdminTab,
  Player,
  Character,
  CharacterSheet,
  CharacterClass,
  Item,
  Spell,
  Game,
  GameOverview,
  GameRule,
  GameCreatePayload,
  PendingJoinRequest,
  Page,
} from './types';
import { ITEM_TYPES, SPELL_TYPES } from './constants';
import { DEFAULT_CLASS_ICON, DEFAULT_ITEM_ICON, DEFAULT_SPELL_ICON, isKitIcon } from './constants/pictograms';
import { API_URL } from './utils/api';
import { evaluateStatFormula } from './utils/formulas';
import { bonusSources, isStackableItem, ITEM_STAT_MODIFIERS, totalAttackDamage } from './utils/itemStats';
import { mediaSrc } from './utils/media';
import { Toast, ItemEffectModifiers } from './components';
import { Pictogram } from './components/Pictogram';
import { PictogramPicker } from './components/PictogramPicker';
import { AppContext } from './context/AppContext';
import type { AppContextValue } from './context/types';
import { ApiError, errorMessage } from './api/client';
import { fetchAdminSession, loginAdmin, logoutAdmin } from './api/admin';
import { createGame, deleteGame } from './api/games';
import { AppRoutes } from './routes/AppRoutes';
import {
  pageFromPath,
  pathFromPage,
  paths,
  parseGameIdFromPath,
  tabFromPath,
} from './navigation/paths';
import {
  clearTableSession,
  readTablePending,
  readTablePlayers,
  rememberTablePlayer,
  updateTablePlayer,
  writeTablePending,
  writeTablePlayers,
  type TablePlayer,
} from './tableSession';

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = pageFromPath(location.pathname);
  const adminTab = tabFromPath(location.pathname);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('adminToken'));
  const [showRules, setShowRules] = useState(false);
  
  // Login states
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // Data states
  const [games, setGames] = useState<Game[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [spells, setSpells] = useState<Spell[]>([]);
  const [classes, setClasses] = useState<CharacterClass[]>([]);
  const [rules, setRules] = useState<GameRule[]>([]);
  
  // Game Overview states
  const [currentGameId, setCurrentGameId] = useState<number | null>(null);
  const [gameOverview, setGameOverview] = useState<GameOverview | null>(null);
  const [overviewLoadError, setOverviewLoadError] = useState<string | null>(null);
  const seenJoinRequestIds = useRef(new Set<number>());
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  
  // Player Registration states
  const [playerName, setPlayerName] = useState('');
  const [playerId, setPlayerId] = useState<number | null>(null);
  const [activeGames, setActiveGames] = useState<Game[]>([]);
  const [pendingRequest, setPendingRequest] = useState<PendingJoinRequest | null>(null);
  
  // Character Selection & Sheet states
  const [selectedGameForChar, setSelectedGameForChar] = useState<number | null>(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState<number | null>(null);
  const [availableCharacters, setAvailableCharacters] = useState<Character[]>([]);
  const [characterSheet, setCharacterSheet] = useState<CharacterSheet | null>(null);
  
  // Level Up Distribution states
  const [statDistribution, setStatDistribution] = useState<{
    strength: number;
    dexterity: number;
    intelligence: number;
    constitution: number;
  }>({ strength: 0, dexterity: 0, intelligence: 0, constitution: 0 });
  
  // Character Creation Modal states
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const [editingCharacterId, setEditingCharacterId] = useState<number | null>(null);
  const [newCharacter, setNewCharacter] = useState({
    name: '',
    class: 'Krieger',
    level: 1,
    health: 100,
    max_health: 100,
    mana: 50,
    max_mana: 50,
    strength: 10,
    dexterity: 10,
    intelligence: 10,
    constitution: 10,
    description: '',
    special_ability: '',
    damage_formula: '5+strength*0.5',
    health_formula: '50+constitution*5',
    mana_formula: '20+intelligence*3',
    movement_formula: '3+dexterity*0.1',
    item_ids: [] as number[],
    spell_ids: [] as number[],
  });
  
  // State for showing formula popup (only one at a time)
  const [activeFormulaPopup, setActiveFormulaPopup] = useState<string | null>(null);
  
  // Handle formula popup toggle (for mobile tap)
  const handleFormulaToggle = (formulaType: string) => {
    setActiveFormulaPopup(prev => prev === formulaType ? null : formulaType);
  };
  
  // Item Creation Modal states
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [newItem, setNewItem] = useState({
    name: '',
    type: 'Waffe',
    icon: '',
    icon_url: DEFAULT_ITEM_ICON,
    description: '',
    effect: '',
    price: 0,
    essence_price: 0,
    strength_bonus: 0,
    dexterity_bonus: 0,
    intelligence_bonus: 0,
    constitution_bonus: 0,
    health_bonus: 0,
    damage_bonus: 0,
  });
  
  // Spell Creation Modal states
  const [showSpellModal, setShowSpellModal] = useState(false);
  const [editingSpellId, setEditingSpellId] = useState<number | null>(null);
  const [newSpell, setNewSpell] = useState({
    name: '',
    type: 'Feuer',
    icon: '',
    icon_url: DEFAULT_SPELL_ICON,
    level: 1,
    description: '',
    effect: '',
    mana_cost: 10
  });
  
  // Spell formula popup state (for character sheet)
  const [hoveredSpellId, setHoveredSpellId] = useState<number | null>(null);
  // Spell formula popup state (for admin overview)
  const [showSpellFormulaPopup, setShowSpellFormulaPopup] = useState<number | null>(null);

  const showToast = (message: string, ms = 3000) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), ms);
  };

  const setCurrentPage = ((action: React.SetStateAction<Page>) => {
    const next = typeof action === 'function' ? action(currentPage) : action;
    const gameId = currentGameId ?? selectedGameForChar ?? parseGameIdFromPath(location.pathname);
    navigate(pathFromPage(next, { gameId, tab: adminTab }));
  }) as React.Dispatch<React.SetStateAction<Page>>;

  const setAdminTab = ((action: React.SetStateAction<AdminTab>) => {
    const next = typeof action === 'function' ? action(adminTab) : action;
    navigate(paths.masterTab(next));
  }) as React.Dispatch<React.SetStateAction<AdminTab>>;

  const restorePlayerIdentity = () => {
    const savedPlayerId = localStorage.getItem('playerId');
    const savedPlayerName = localStorage.getItem('playerName');
    const savedPendingRequest = localStorage.getItem('pendingRequest');
    const savedSelectedCharacterId = localStorage.getItem('selectedCharacterId');
    const savedSelectedGameForChar = localStorage.getItem('selectedGameForChar');

    if (!savedPlayerId || !savedPlayerName) return;

    setPlayerId(parseInt(savedPlayerId, 10));
    setPlayerName(savedPlayerName);

    if (savedSelectedCharacterId) {
      setSelectedCharacterId(parseInt(savedSelectedCharacterId, 10));
    }
    if (savedSelectedGameForChar) {
      setSelectedGameForChar(parseInt(savedSelectedGameForChar, 10));
    }
    if (savedPendingRequest) {
      try {
        setPendingRequest(JSON.parse(savedPendingRequest));
      } catch {
        localStorage.removeItem('pendingRequest');
      }
    }
  };

  // Session: Identität aus localStorage, Ort aus der URL
  useEffect(() => {
    restorePlayerIdentity();

    const boot = async () => {
      const token = localStorage.getItem('adminToken');
      const onMaster = location.pathname.startsWith('/master');
      if (token && onMaster) {
        try {
          await fetchAdminSession();
          setIsAuthenticated(true);
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) {
            localStorage.removeItem('adminToken');
            setIsAuthenticated(false);
            navigate(paths.login, { replace: true });
          }
        }
      } else if (token) {
        setIsAuthenticated(true);
      }
    };

    void boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pathGameId = parseGameIdFromPath(location.pathname);
    if (/^\/master\/games\/\d+/.test(location.pathname) && pathGameId) {
      setCurrentGameId(pathGameId);
      fetchGameOverview(pathGameId);
    }
    if (/^\/play\/select\/\d+/.test(location.pathname) && pathGameId) {
      setSelectedGameForChar(pathGameId);
      fetchAvailableCharacters(pathGameId);
    }
    if (location.pathname === paths.hero) {
      const pid = playerId ?? (localStorage.getItem('playerId') ? parseInt(localStorage.getItem('playerId') as string, 10) : null);
      if (pid) fetchCharacterSheet(pid, false);
    }
    if (location.pathname === paths.play || location.pathname.startsWith('/play/join/')) {
      fetchActiveGames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  
  useEffect(() => {
    if (playerId && playerName) {
      localStorage.setItem('playerId', playerId.toString());
      localStorage.setItem('playerName', playerName);
    }
  }, [playerId, playerName]);
  
  useEffect(() => {
    if (pendingRequest) {
      localStorage.setItem('pendingRequest', JSON.stringify(pendingRequest));
    } else {
      localStorage.removeItem('pendingRequest');
    }
  }, [pendingRequest]);
  
  useEffect(() => {
    if (selectedCharacterId) {
      localStorage.setItem('selectedCharacterId', selectedCharacterId.toString());
    } else {
      localStorage.removeItem('selectedCharacterId');
    }
  }, [selectedCharacterId]);
  
  useEffect(() => {
    if (selectedGameForChar) {
      localStorage.setItem('selectedGameForChar', selectedGameForChar.toString());
    } else {
      localStorage.removeItem('selectedGameForChar');
    }
  }, [selectedGameForChar]);

  // Fetch data
  const fetchGames = async () => {
    try {
      const response = await fetch(`${API_URL}/api/games`);
      const data = await response.json();
      setGames(data);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/players`);
      const data = await response.json();
      setPlayers(data);
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };

  const fetchCharacters = async () => {
    try {
      const response = await fetch(`${API_URL}/api/characters`);
      const data = await response.json();
      setCharacters(data);
    } catch (error) {
      console.error('Error fetching characters:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetch(`${API_URL}/api/items`);
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchRules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rules`);
      if (response.ok) {
        const data = await response.json();
        setRules(data);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
    }
  };

  const fetchSpells = async () => {
    try {
      const response = await fetch(`${API_URL}/api/spells`);
      const data = await response.json();
      setSpells(data);
    } catch (error) {
      console.error('Error fetching spells:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/classes`);
      const data = await response.json();
      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && currentPage === 'admin-panel') {
      fetchGames();
      fetchPlayers();
      fetchCharacters();
      fetchClasses();
      fetchItems();
      fetchSpells();
      fetchRules();
    }
  }, [isAuthenticated, currentPage]);

  // Load rules on landing page
  useEffect(() => {
    if (currentPage === 'landing') {
      fetchRules();
    }
  }, [currentPage]);

  const handleAdminLogin = async (username: string, password: string) => {
    try {
      const data = await loginAdmin(username, password);
      if (data.success && data.token) {
        localStorage.setItem('adminToken', data.token);
        setIsAuthenticated(true);
        navigate(paths.masterGames);
      } else {
        showToast('Ungültige Anmeldedaten');
      }
    } catch (error) {
      showToast(errorMessage(error, 'Fehler beim Anmelden'));
    }
  };
  
  const handleLogout = () => {
    void logoutAdmin().catch(() => undefined);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('currentGameId');
    setIsAuthenticated(false);
    setCurrentGameId(null);
    navigate(paths.landing);
  };

  const handleDeleteGame = async (id: number) => {
    try {
      await deleteGame(id);
      fetchGames();
      showToast('Spiel gelöscht');
    } catch (error) {
      showToast(errorMessage(error, 'Fehler beim Löschen'));
    }
  };

  const handleDeletePlayer = async (id: number) => {
    if (!window.confirm('Spieler wirklich loeschen?')) return;
    try {
      await fetch(`${API_URL}/api/players/${id}`, { method: 'DELETE' });
      fetchPlayers();
    } catch (error) {
      alert('Fehler beim Loeschen');
    }
  };

  const handleDeleteCharacter = async (id: number) => {
    if (!window.confirm('Charakter wirklich loeschen?')) return;
    try {
      await fetch(`${API_URL}/api/characters/${id}`, { method: 'DELETE' });
      fetchCharacters();
    } catch (error) {
      alert('Fehler beim Loeschen');
    }
  };

  const handleDeleteItem = async (id: number) => {
    if (!window.confirm('Item wirklich loeschen?')) return;
    try {
      await fetch(`${API_URL}/api/items/${id}`, { method: 'DELETE' });
      fetchItems();
    } catch (error) {
      alert('Fehler beim Loeschen');
    }
  };

  // Rules management handlers
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [newRule, setNewRule] = useState({
    title: '',
    content: '',
    order_index: 0
  });

  const handleAddRule = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(newRule)
      });
      
      if (response.ok) {
        await fetchRules();
        setNewRule({ title: '', content: '', order_index: 0 });
        setIsAddingRule(false);
        setToastMessage('Regel erfolgreich erstellt');
        setTimeout(() => setToastMessage(null), 3000);
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.error}`);
      }
    } catch (error) {
      console.error('Error adding rule:', error);
      alert('Fehler beim Erstellen der Regel');
    }
  };

  const handleEditRule = (rule: GameRule) => {
    setEditingRuleId(rule.id);
    setIsAddingRule(false);
    setNewRule({
      title: rule.title,
      content: rule.content,
      order_index: rule.order_index
    });
  };

  const handleUpdateRule = async () => {
    if (!editingRuleId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/rules/${editingRuleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify(newRule)
      });
      
      if (response.ok) {
        await fetchRules();
        setEditingRuleId(null);
        setIsAddingRule(false);
        setNewRule({ title: '', content: '', order_index: 0 });
        setToastMessage('Regel erfolgreich aktualisiert');
        setTimeout(() => setToastMessage(null), 3000);
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.error}`);
      }
    } catch (error) {
      console.error('Error updating rule:', error);
      alert('Fehler beim Aktualisieren der Regel');
    }
  };

  const handleDeleteRule = async (id: number) => {
    if (!window.confirm('Möchtest du diese Regel wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/rules/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (response.ok) {
        await fetchRules();
        setToastMessage('Regel erfolgreich gelöscht');
        setTimeout(() => setToastMessage(null), 3000);
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('Fehler beim Löschen der Regel');
    }
  };

  const handleCancelRuleEdit = () => {
    setEditingRuleId(null);
    setIsAddingRule(false);
    setNewRule({ title: '', content: '', order_index: 0 });
  };

  // Classes management handlers
  const [editingClassId, setEditingClassId] = useState<number | null>(null);
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClass, setNewClass] = useState({ name: '', icon: DEFAULT_CLASS_ICON, order_index: 0 });

  const handleAddClass = async () => {
    if (!newClass.name.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/classes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newClass)
      });
      if (response.ok) {
        await fetchClasses();
        setNewClass({ name: '', icon: DEFAULT_CLASS_ICON, order_index: classes.length });
        setIsAddingClass(false);
        setToastMessage('Klasse erfolgreich erstellt');
        setTimeout(() => setToastMessage(null), 3000);
      } else {
        const err = await response.json();
        alert(err.error || 'Fehler');
      }
    } catch (e) {
      console.error(e);
      alert('Fehler beim Erstellen');
    }
  };

  const handleEditClass = (c: CharacterClass) => {
    setEditingClassId(c.id);
    setIsAddingClass(false);
    setNewClass({ name: c.name, icon: c.icon || DEFAULT_CLASS_ICON, order_index: c.order_index });
  };

  const handleUpdateClass = async () => {
    if (!editingClassId) return;
    if (!newClass.name.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/classes/${editingClassId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newClass)
      });
      if (response.ok) {
        await fetchClasses();
        setEditingClassId(null);
        setNewClass({ name: '', icon: DEFAULT_CLASS_ICON, order_index: 0 });
        setToastMessage('Klasse erfolgreich aktualisiert');
        setTimeout(() => setToastMessage(null), 3000);
      } else {
        const err = await response.json();
        alert(err.error || 'Fehler');
      }
    } catch (e) {
      console.error(e);
      alert('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteClass = async (id: number) => {
    if (!window.confirm('Klasse wirklich loeschen?')) return;
    try {
      const response = await fetch(`${API_URL}/api/classes/${id}`, { method: 'DELETE' });
      if (response.ok) {
        await fetchClasses();
        setEditingClassId(null);
        setToastMessage('Klasse geloescht');
        setTimeout(() => setToastMessage(null), 3000);
      } else {
        const err = await response.json();
        alert(err.error || 'Fehler');
      }
    } catch (e) {
      alert('Fehler beim Loeschen');
    }
  };

  const handleCancelClassEdit = () => {
    setEditingClassId(null);
    setIsAddingClass(false);
    setNewClass({ name: '', icon: DEFAULT_CLASS_ICON, order_index: 0 });
  };

  const handleDeleteSpell = async (id: number) => {
    if (!window.confirm('Zauber wirklich loeschen?')) return;
    try {
      await fetch(`${API_URL}/api/spells/${id}`, { method: 'DELETE' });
      fetchSpells();
    } catch (error) {
      alert('Fehler beim Loeschen');
    }
  };

  const handleCreateGame = async (payload: GameCreatePayload) => {
    const newGame = await createGame(payload);
    if (newGame?.id) {
      setCurrentGameId(newGame.id);
      fetchGameOverview(newGame.id);
      showToast(`Spiel „${newGame.name}“ erstellt`);
      navigate(paths.masterGame(newGame.id));
    }
    fetchGames();
  };
  
  const fetchGameOverview = async (gameId: number, options?: { background?: boolean }) => {
    const background = options?.background === true;
    if (!background) setOverviewLoadError(null);
    try {
      const response = await fetch(`${API_URL}/api/games/${gameId}/overview`);
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || `Fehler ${response.status}`);
      }
      const data = await response.json();
      
      // Ensure data structure is valid
      if (!data || !data.game) {
        console.error('Invalid game overview data:', data);
        if (background) return;
        setOverviewLoadError('Ungültige Antwort vom Server.');
        setGameOverview(null);
        return;
      }
      
      setGameOverview(data);
      if (background) setOverviewLoadError(null);
      
      const pending = Array.isArray(data.pending_join_requests) ? data.pending_join_requests : [];
      const fresh = pending.filter((request: { id?: number; player_name?: string }) => {
        if (typeof request?.id !== 'number') return false;
        if (seenJoinRequestIds.current.has(request.id)) return false;
        seenJoinRequestIds.current.add(request.id);
        return true;
      });
      const newest = fresh[0];
      if (newest?.player_name) {
        setToastMessage(`${newest.player_name} moechte dem Spiel beitreten!`);
        setTimeout(() => setToastMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error fetching game overview:', error);
      if (background) return;
      setGameOverview(null);
      setOverviewLoadError(error instanceof Error ? error.message : 'Spielübersicht konnte nicht geladen werden.');
    }
  };
  
  const handleAcceptJoinRequest = async (requestId: number) => {
    if (!currentGameId) return;
    try {
      await fetch(`${API_URL}/api/games/${currentGameId}/join-requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'accepted' }),
      });
      fetchGameOverview(currentGameId);
      setToastMessage('Spieler akzeptiert!');
      setTimeout(() => setToastMessage(null), 3000);
    } catch (error) {
      alert('Fehler beim Akzeptieren');
    }
  };
  
  const handleRejectJoinRequest = async (requestId: number) => {
    if (!currentGameId) return;
    try {
      await fetch(`${API_URL}/api/games/${currentGameId}/join-requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'rejected' }),
      });
      fetchGameOverview(currentGameId);
      setToastMessage('Spieler abgelehnt');
      setTimeout(() => setToastMessage(null), 3000);
    } catch (error) {
      alert('Fehler beim Ablehnen');
    }
  };
  
  // Level Up Handlers
  const handleLevelUp = async (characterId: number, skillPoints: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/characters/${characterId}/level-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ skill_points: skillPoints }),
      });
      if (response.ok) {
        setToastMessage(`Level Up gewaehrt! (${skillPoints} SP)`);
        setTimeout(() => setToastMessage(null), 3000);
        if (currentGameId) fetchGameOverview(currentGameId);
      } else {
        alert('Fehler beim Level Up');
      }
    } catch (error) {
      alert('Fehler beim Level Up');
    }
  };
  
  const handleBulkLevelUp = async (characterIds: number[], skillPoints: number) => {
    if (!currentGameId) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/games/${currentGameId}/bulk-level-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ character_ids: characterIds, skill_points: skillPoints }),
      });
      if (response.ok) {
        const data = await response.json();
        setToastMessage(`${data.updated_count} Charaktere erhalten Level Up!`);
        setTimeout(() => setToastMessage(null), 3000);
        fetchGameOverview(currentGameId);
      } else {
        alert('Fehler beim Bulk Level Up');
      }
    } catch (error) {
      alert('Fehler beim Bulk Level Up');
    }
  };
  
  const handleResetLevel = async (characterId: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/characters/${characterId}/reset-level`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        setToastMessage('Level zurueckgesetzt!');
        setTimeout(() => setToastMessage(null), 3000);
        if (currentGameId) fetchGameOverview(currentGameId);
      } else {
        alert('Fehler beim Reset');
      }
    } catch (error) {
      alert('Fehler beim Reset');
    }
  };
  
  const handleSetLevel = async (characterId: number, newLevel: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/characters/${characterId}/set-level`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ level: newLevel }),
      });
      if (response.ok) {
        setToastMessage(`Level auf ${newLevel} gesetzt!`);
        setTimeout(() => setToastMessage(null), 3000);
        if (currentGameId) fetchGameOverview(currentGameId);
      } else {
        alert('Fehler beim Setzen des Levels');
      }
    } catch (error) {
      alert('Fehler beim Setzen des Levels');
    }
  };
  
  const handleAssignItem = async (characterId: number, itemId: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/characters/${characterId}/assign-item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ item_id: itemId }),
      });
      if (response.ok) {
        setToastMessage('Item zugewiesen und Stats aktualisiert!');
        setTimeout(() => setToastMessage(null), 3000);
        if (currentGameId) fetchGameOverview(currentGameId);
      } else {
        const errorData = await response.json();
        alert(`Fehler: ${errorData.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      alert('Fehler beim Zuweisen des Items');
    }
  };
  
  const handleUseCharacterItem = async (characterId: number, characterItemId: number, count = 1) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/characters/${characterId}/use-item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ character_item_id: characterItemId, count }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setToastMessage(data.message || 'Item verwendet');
        setTimeout(() => setToastMessage(null), 3000);
        if (currentGameId) fetchGameOverview(currentGameId);
      } else {
        alert(data.error || 'Item konnte nicht verwendet werden');
      }
    } catch (error) {
      alert('Fehler beim Verwenden des Items');
    }
  };

  const handleRemoveItem = async (characterId: number, itemId: number, characterItemId?: number, count = 1) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/characters/${characterId}/remove-item`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          item_id: itemId,
          character_item_id: characterItemId,
          count,
        }),
      });
      if (response.ok) {
        setToastMessage('Item entfernt und Stats aktualisiert!');
        setTimeout(() => setToastMessage(null), 3000);
        if (currentGameId) fetchGameOverview(currentGameId);
      } else {
        const errorData = await response.json();
        alert(`Fehler: ${errorData.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      alert('Fehler beim Entfernen des Items');
    }
  };
  
  const handleAssignSpell = async (characterId: number, spellId: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/characters/${characterId}/assign-spell`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ spell_id: spellId }),
      });
      if (response.ok) {
        setToastMessage('Zauber zugewiesen!');
        setTimeout(() => setToastMessage(null), 3000);
        if (currentGameId) fetchGameOverview(currentGameId);
      } else {
        const errorData = await response.json();
        alert(`Fehler: ${errorData.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      alert('Fehler beim Zuweisen des Zaubers');
    }
  };
  
  const handleRemoveSpell = async (characterId: number, spellId: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/characters/${characterId}/remove-spell`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ spell_id: spellId }),
      });
      if (response.ok) {
        setToastMessage('Zauber entfernt!');
        setTimeout(() => setToastMessage(null), 3000);
        if (currentGameId) fetchGameOverview(currentGameId);
      } else {
        const errorData = await response.json();
        alert(`Fehler: ${errorData.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      alert('Fehler beim Entfernen des Zaubers');
    }
  };
  
  const handleSetHealthMana = async (characterId: number, health: number | null, mana: number | null) => {
    try {
      const token = localStorage.getItem('adminToken');
      const body: {health?: number; mana?: number} = {};
      if (health !== null) body.health = health;
      if (mana !== null) body.mana = mana;
      
      const response = await fetch(`${API_URL}/api/characters/${characterId}/set-health-mana`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const messages = [];
        if (health !== null) messages.push(`Health: ${health}`);
        if (mana !== null) messages.push(`Mana: ${mana}`);
        setToastMessage(`${messages.join(', ')} gesetzt!`);
        setTimeout(() => setToastMessage(null), 3000);
        if (currentGameId) fetchGameOverview(currentGameId);
      } else {
        const errorData = await response.json();
        alert(`Fehler: ${errorData.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      alert('Fehler beim Setzen von Health/Mana');
    }
  };
  
  const routeRegisteredPlayer = (player: {
    id: number;
    name: string;
    existing?: boolean;
    has_character?: boolean;
    character_id?: number;
    game_id?: number | null;
  }) => {
    setPlayerId(player.id);
    setPlayerName(player.name);
    if (player.existing && player.has_character && player.character_id) {
      setToastMessage(`Willkommen zurueck, ${player.name}!`);
      setTimeout(() => setToastMessage(null), 3000);
      setSelectedCharacterId(player.character_id);
      localStorage.setItem('selectedCharacterId', player.character_id.toString());
      if (player.game_id) {
        setSelectedGameForChar(player.game_id);
        localStorage.setItem('selectedGameForChar', player.game_id.toString());
      }
      fetchCharacterSheet(player.id);
      navigate(paths.hero);
      return;
    }
    if (player.existing && player.game_id) {
      setToastMessage('Willkommen zurück! Wähle deinen Charakter.');
      setTimeout(() => setToastMessage(null), 3000);
      setSelectedGameForChar(player.game_id);
      localStorage.setItem('selectedGameForChar', player.game_id.toString());
      fetchAvailableCharacters(player.game_id);
      navigate(paths.playSelect(player.game_id));
      return;
    }
    if (player.existing) {
      setToastMessage(`Willkommen zurueck, ${player.name}!`);
      setTimeout(() => setToastMessage(null), 3000);
    }
    fetchActiveGames();
    navigate(paths.play);
  };

  const createPlayerRecord = async (name: string) => {
    const response = await fetch(`${API_URL}/api/players`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email: `${name}@player.local` }),
    });
    if (!response.ok) return null;
    return response.json();
  };

  const storeRegisteredPlayers = (players: Array<{ id: number; name: string; game_id?: number | null; has_character?: boolean }>) => {
    const entries: TablePlayer[] = players.map((player) => ({
      id: player.id,
      name: player.name,
      gameId: player.game_id ?? null,
      hasCharacter: Boolean(player.has_character),
    }));
    writeTablePlayers(entries);
  };

  // Player Registration Handlers
  const handlePlayerNameSubmit = async () => {
    if (!playerName.trim()) {
      alert('Bitte gib einen Namen ein!');
      return;
    }
    await handlePlayerGroupSubmit([playerName]);
  };

  const handlePlayerGroupSubmit = async (names: string[]) => {
    const unique = Array.from(new Set(names.map((name) => name.trim()).filter(Boolean)));
    if (unique.length === 0) {
      alert('Bitte gib einen Namen ein!');
      return;
    }
    try {
      const created = [];
      for (const name of unique) {
        const player = await createPlayerRecord(name);
        if (!player) {
          alert('Fehler beim Erstellen des Spielers');
          return;
        }
        created.push(player);
      }
      storeRegisteredPlayers(created);
      const active = created.find((player) => !(player.existing && player.has_character)) || created[0];
      routeRegisteredPlayer(active);
    } catch (error) {
      alert('Fehler beim Erstellen des Spielers');
    }
  };

  const addCompanion = async (rawName: string) => {
    const name = rawName.trim();
    if (!name) {
      alert('Bitte gib einen Namen ein!');
      return;
    }
    try {
      const player = await createPlayerRecord(name);
      if (!player) {
        alert('Fehler beim Erstellen des Spielers');
        return;
      }
      if (playerId && playerName && !readTablePlayers().some((entry) => entry.id === playerId)) {
        rememberTablePlayer({
          id: playerId,
          name: playerName,
          gameId: selectedGameForChar,
          hasCharacter: Boolean(characterSheet),
        });
      }
      rememberTablePlayer({
        id: player.id,
        name: player.name || name,
        gameId: player.game_id ?? null,
        hasCharacter: Boolean(player.has_character),
      });
      if (player.has_character) {
        setToastMessage(`${player.name} ist auf diesem Gerät.`);
        setTimeout(() => setToastMessage(null), 3000);
        if (location.pathname !== paths.hero) navigate(paths.hero);
        return;
      }
      setPlayerId(player.id);
      setPlayerName(player.name || name);
      if (player.game_id) {
        setSelectedGameForChar(player.game_id);
        localStorage.setItem('selectedGameForChar', String(player.game_id));
        fetchAvailableCharacters(player.game_id);
        navigate(paths.playSelect(player.game_id));
        return;
      }
      const shared = readTablePlayers().find((entry) => entry.id !== player.id && entry.gameId);
      if (shared?.gameId) {
        const gameName = activeGames.find((game) => game.id === shared.gameId)?.name || 'Spiel';
        const response = await fetch(`${API_URL}/api/games/${shared.gameId}/join-requests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ player_id: player.id }),
        });
        if (response.ok) {
          const request = await response.json();
          setPendingRequest({ gameId: shared.gameId, gameName, requestId: request.id });
          setToastMessage(`Beitrittsanfrage für ${player.name} an "${gameName}" gesendet!`);
          setTimeout(() => setToastMessage(null), 3000);
          navigate(paths.playJoin(shared.gameId));
          return;
        }
      }
      setToastMessage(`${player.name} kann jetzt einem Spiel beitreten.`);
      setTimeout(() => setToastMessage(null), 3000);
      fetchActiveGames();
      navigate(paths.play);
    } catch (error) {
      alert('Fehler beim Erstellen des Spielers');
    }
  };
  
  const fetchActiveGames = async () => {
    try {
      const response = await fetch(`${API_URL}/api/games/active`);
      const data = await response.json();
      setActiveGames(data);
    } catch (error) {
      console.error('Error fetching active games:', error);
    }
  };
  
  // Auto-refresh for player view (every 5 seconds)
  useEffect(() => {
    if (currentPage === 'player-view' && playerId) {
      const interval = setInterval(async () => {
        fetchActiveGames();
        
        // Check if join request was accepted
        const tableWaiting = readTablePending();
        if (tableWaiting.length > 1) {
          try {
            let remaining = [...tableWaiting];
            for (const req of tableWaiting) {
              const response = await fetch(`${API_URL}/api/games/${req.gameId}/join-requests`);
              const requests = await response.json();
              const mine = requests.find((entry: { id: number; status: string }) => entry.id === req.requestId);
              if (mine && mine.status === 'accepted') {
                updateTablePlayer(req.playerId, { gameId: req.gameId });
                remaining = remaining.filter((entry) => entry.requestId !== req.requestId);
              }
            }
            writeTablePending(remaining);
            if (remaining.length === 0) {
              setPendingRequest(null);
              const next = readTablePlayers().find((entry) => entry.gameId && !entry.hasCharacter);
              if (next && next.gameId) {
                setPlayerId(next.id);
                setPlayerName(next.name);
                setSelectedGameForChar(next.gameId);
                setToastMessage('Anfragen akzeptiert! Wähle den ersten Helden.');
                setTimeout(() => setToastMessage(null), 3000);
                fetchAvailableCharacters(next.gameId);
                navigate(paths.playSelect(next.gameId));
              }
            }
          } catch (error) {
            console.error('Error checking request status:', error);
          }
        } else if (pendingRequest) {
          try {
            const response = await fetch(`${API_URL}/api/games/${pendingRequest.gameId}/join-requests`);
            const requests = await response.json();
            const myRequest = requests.find((r: any) => r.id === pendingRequest.requestId);
            
            if (myRequest && myRequest.status === 'accepted') {
              updateTablePlayer(playerId || 0, { gameId: pendingRequest.gameId });
              setPendingRequest(null);
              setSelectedGameForChar(pendingRequest.gameId);
              setToastMessage('Anfrage akzeptiert! Wähle deinen Charakter.');
              setTimeout(() => setToastMessage(null), 3000);
              fetchAvailableCharacters(pendingRequest.gameId);
              navigate(paths.playSelect(pendingRequest.gameId));
            }
          } catch (error) {
            console.error('Error checking request status:', error);
          }
        }
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [currentPage, playerId, pendingRequest, navigate]);
  
  // Auto-refresh for character sheet (every 5 seconds)
  useEffect(() => {
    if (currentPage === 'character-sheet' && playerId && characterSheet) {
      const interval = setInterval(async () => {
        try {
          const response = await fetch(`${API_URL}/api/players/${playerId}/character`);
          if (response.ok) {
            const data = await response.json();
            
            // Check if new level up was granted
            if (!characterSheet.character.pending_level_up && data.character.pending_level_up) {
              setToastMessage(`⬆️ Level Up verfuegbar! Du hast ${data.character.pending_skill_points} Skill Points erhalten!`);
              setTimeout(() => setToastMessage(null), 5000);
              // Reset stat distribution when new level up arrives
              setStatDistribution({ strength: 0, dexterity: 0, intelligence: 0, constitution: 0 });
            }
            
            // Update character sheet WITHOUT resetting stat distribution
            setCharacterSheet(data);
          }
        } catch (error) {
          console.error('Error refreshing character sheet:', error);
        }
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [currentPage, playerId, characterSheet]);
  
  // Auto-refresh for game overview (every 3 seconds)
  useEffect(() => {
    if (currentPage === 'game-overview' && currentGameId) {
      const interval = setInterval(() => {
        fetchGameOverview(currentGameId, { background: true });
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [currentPage, currentGameId]);
  
  const handleJoinGameRequest = async (gameId: number, gameName: string) => {
    const mates = readTablePlayers().filter((entry) => !entry.gameId && !entry.hasCharacter);
    const targets = mates.length > 1 ? mates : (playerId ? [{ id: playerId, name: playerName }] : []);
    if (targets.length === 0) {
      alert('Bitte gib zuerst einen Namen ein!');
      return;
    }
    
    try {
      const created = [];
      for (const target of targets) {
        const response = await fetch(`${API_URL}/api/games/${gameId}/join-requests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ player_id: target.id }),
        });
        if (!response.ok) {
          alert('Fehler beim Senden der Anfrage');
          return;
        }
        const request = await response.json();
        created.push({
          gameId,
          gameName,
          requestId: request.id,
          playerId: target.id,
          playerName: target.name,
        });
      }
      writeTablePending(created.length > 1 ? created : []);
      setPendingRequest({ gameId, gameName, requestId: created[0].requestId });
      const who = created.length > 1 ? created.map((entry) => entry.playerName).join(', ') : '';
      setToastMessage(who ? `Beitrittsanfrage für ${who} an "${gameName}" gesendet!` : `Beitrittsanfrage an "${gameName}" gesendet!`);
      setTimeout(() => setToastMessage(null), 3000);
      navigate(paths.playJoin(gameId));
    } catch (error) {
      alert('Fehler beim Senden der Anfrage');
    }
  };
  
  const handleCancelRequest = async () => {
    const waiting = readTablePending();
    const requests = waiting.length > 0
      ? waiting
      : (pendingRequest && playerId ? [{ ...pendingRequest, playerId, playerName }] : []);
    if (requests.length === 0) return;
    
    try {
      for (const req of requests) {
        const response = await fetch(`${API_URL}/api/games/${req.gameId}/join-requests/${req.requestId}`, {
          method: 'DELETE',
        });
        if (!response.ok) {
          alert('Fehler beim Abbrechen der Anfrage');
          return;
        }
      }
      writeTablePending([]);
      setPendingRequest(null);
      setToastMessage('Anfrage abgebrochen');
      setTimeout(() => setToastMessage(null), 3000);
      fetchActiveGames();
      navigate(paths.play);
    } catch (error) {
      alert('Fehler beim Abbrechen der Anfrage');
    }
  };
  
  const handlePlayerLogout = () => {
    localStorage.removeItem('playerId');
    localStorage.removeItem('playerName');
    localStorage.removeItem('pendingRequest');
    localStorage.removeItem('selectedCharacterId');
    localStorage.removeItem('selectedGameForChar');
    clearTableSession();
    setPlayerId(null);
    setPlayerName('');
    setPendingRequest(null);
    setCharacterSheet(null);
    navigate(paths.landing);
  };
  
  // Character Selection Handlers
  const fetchAvailableCharacters = async (gameId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/games/${gameId}/available-characters`);
      const data = await response.json();
      setAvailableCharacters(data);
    } catch (error) {
      console.error('Error fetching characters:', error);
    }
  };
  
  const handleSelectCharacter = async (characterId: number) => {
    if (!playerId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/characters/${characterId}/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedCharacterId(data.character_id);
        localStorage.setItem('selectedCharacterId', data.character_id.toString());
        updateTablePlayer(playerId, { hasCharacter: true, gameId: selectedGameForChar });
        const next = readTablePlayers().find((entry) => entry.gameId && !entry.hasCharacter && entry.id !== playerId);
        if (next && next.gameId) {
          setPlayerId(next.id);
          setPlayerName(next.name);
          setSelectedGameForChar(next.gameId);
          setToastMessage(`${next.name} wählt jetzt einen Helden.`);
          setTimeout(() => setToastMessage(null), 3000);
          fetchAvailableCharacters(next.gameId);
          navigate(paths.playSelect(next.gameId));
          return;
        }
        setToastMessage('Charakter ausgewaehlt!');
        setTimeout(() => setToastMessage(null), 3000);
        fetchCharacterSheet(playerId);
      } else if (response.status === 400) {
        // Player already has a character - redirect to character sheet
        const errorData = await response.json();
        if (errorData.existing_character_id) {
          setSelectedCharacterId(errorData.existing_character_id);
          localStorage.setItem('selectedCharacterId', errorData.existing_character_id.toString());
          setToastMessage('Du hast bereits einen Charakter!');
          setTimeout(() => setToastMessage(null), 3000);
          fetchCharacterSheet(playerId);
        } else {
          console.error('Character selection error:', errorData);
          alert(`Fehler: ${errorData.error || 'Unbekannter Fehler'}`);
        }
      } else {
        const errorData = await response.json();
        console.error('Character selection error:', errorData);
        alert(`Fehler beim Auswaehlen des Charakters: ${errorData.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Character selection exception:', error);
      alert(`Fehler beim Auswaehlen des Charakters: ${error}`);
    }
  };
  
  const fetchCharacterSheet = async (pId?: number, resetStats = true) => {
    const playerIdToUse = pId || playerId;
    if (!playerIdToUse) return;
    
    try {
      const response = await fetch(`${API_URL}/api/players/${playerIdToUse}/character`);
      if (response.ok) {
        const data = await response.json();
        setCharacterSheet(data);
        if (currentPage !== 'character-sheet') {
          navigate(paths.hero);
        }
        // Reset stat distribution only when explicitly requested (not during auto-refresh)
        if (resetStats) {
          setStatDistribution({ strength: 0, dexterity: 0, intelligence: 0, constitution: 0 });
        }
      } else if (response.status === 404) {
        // No character selected yet
        console.log('No character selected');
      }
    } catch (error) {
      console.error('Error fetching character sheet:', error);
    }
  };
  
  const handleStatIncrease = (stat: 'strength' | 'dexterity' | 'intelligence' | 'constitution') => {
    if (!characterSheet) return;
    
    const totalDistributed = Object.values(statDistribution).reduce((sum, val) => sum + val, 0);
    const availablePoints = characterSheet.character.pending_skill_points || 0;
    
    if (totalDistributed < availablePoints) {
      setStatDistribution(prev => ({
        ...prev,
        [stat]: prev[stat] + 1
      }));
    }
  };
  
  const handleStatDecrease = (stat: 'strength' | 'dexterity' | 'intelligence' | 'constitution') => {
    if (statDistribution[stat] > 0) {
      setStatDistribution(prev => ({
        ...prev,
        [stat]: prev[stat] - 1
      }));
    }
  };
  
  // Evaluate formula in text (e.g., "Macht {5+2*intelligence} Schaden" -> "Macht 15 Schaden")
  const evaluateFormulasInText = (text: string | undefined, char: Character & { basisschaden?: number; schaden?: number }): string => {
    if (!text) return '';
    const base = typeof char.basisschaden === 'number'
      ? char.basisschaden
      : (evaluateStatFormula(char.damage_formula || '5+strength*0.5', char) ?? 0);
    const full = typeof char.schaden === 'number'
      ? char.schaden
      : totalAttackDamage(base, bonusSources(char));
    const caster = { ...char, basisschaden: base, schaden: full };
    try {
      // Find all formulas in curly braces: {formula}
      const formulaPattern = /\{([^}]+)\}/g;
      let result = text;
      let match;
      
      // Reset regex lastIndex to avoid issues with multiple calls
      formulaPattern.lastIndex = 0;
      
      while ((match = formulaPattern.exec(text)) !== null) {
        const formula = match[1];
        try {
          const calculatedValue = evaluateStatFormula(formula, caster);
          if (calculatedValue == null) {
            console.warn("Formula contains invalid characters:", formula);
          } else {
            result = result.replace(match[0], String(calculatedValue));
          }
        } catch (error) {
          console.error("Error evaluating formula in text:", formula, error);
        }
      }
      
      return result;
    } catch (error) {
      console.error("Error evaluating formulas in text:", text, error);
      return text || '';
    }
  };
  
  // Extract formulas from text for display in tooltips
  const extractFormulasFromText = (text: string | undefined): Array<{formula: string, value: string}> => {
    if (!text) return [];
    const formulas: Array<{formula: string, value: string}> = [];
    const formulaPattern = /\{([^}]+)\}/g;
    let match;
    
    while ((match = formulaPattern.exec(text)) !== null) {
      formulas.push({
        formula: match[1],
        value: match[0]
      });
    }
    
    return formulas;
  };
  
  // Calculate damage based on formula
  const calculateDamage = (formula: string | undefined, char: Character, defaultFormula: string = '5+strength*0.5'): number => {
    return evaluateStatFormula(formula || defaultFormula, char) ?? 0;
  };
  
  const handleUseItem = async (characterItemId: number, count = 1) => {
    if (!playerId) return;
    try {
      const response = await fetch(`${API_URL}/api/players/${playerId}/use-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ character_item_id: characterItemId, count }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setToastMessage(data.message || 'Item verwendet');
        setTimeout(() => setToastMessage(null), 3000);
        if (characterSheet) {
          setCharacterSheet({
            ...characterSheet,
            character: {
              ...characterSheet.character,
              health: data.health ?? characterSheet.character.health,
              mana: data.mana ?? characterSheet.character.mana,
            },
            items: data.items ?? characterSheet.items,
            effects: data.effects ?? characterSheet.effects,
          });
        }
        fetchCharacterSheet(playerId, false);
      } else {
        alert(data.error || 'Item konnte nicht verwendet werden');
      }
    } catch (error) {
      alert('Fehler beim Verwenden des Items');
    }
  };

  const handleCommitLevelUp = async () => {
    if (!playerId || !characterSheet) return;
    
    const totalDistributed = Object.values(statDistribution).reduce((sum, val) => sum + val, 0);
    const availablePoints = characterSheet.character.pending_skill_points || 0;
    
    if (totalDistributed !== availablePoints) {
      alert(`Du musst alle ${availablePoints} Skill Points verteilen! (Aktuell: ${totalDistributed})`);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/players/${playerId}/commit-level-up`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stat_increases: statDistribution }),
      });
      
      if (response.ok) {
        setToastMessage('Level Up erfolgreich!');
        setTimeout(() => setToastMessage(null), 3000);
        fetchCharacterSheet(); // Reload character data
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.error || 'Level Up fehlgeschlagen'}`);
      }
    } catch (error) {
      alert('Fehler beim Committen des Level Ups');
    }
  };

  const handleAddPlayer = async () => {
    const name = prompt('Spieler Name:');
    if (!name) return;
    const email = prompt('Email (optional):') || '';
    try {
      await fetch(`${API_URL}/api/players`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email }),
      });
      fetchPlayers();
    } catch (error) {
      alert('Fehler beim Erstellen');
    }
  };

  const handleOpenCharacterModal = () => {
    setEditingCharacterId(null);
    setNewCharacter({
      name: '',
      class: 'Krieger',
      level: 1,
      health: 100,
      max_health: 100,
      mana: 50,
      max_mana: 50,
      strength: 10,
      dexterity: 10,
      intelligence: 10,
      constitution: 10,
      description: '',
      special_ability: '',
      damage_formula: '5+strength*0.5',
      health_formula: '50+constitution*5',
      mana_formula: '20+intelligence*3',
      movement_formula: '3+dexterity*0.1',
      item_ids: [],
      spell_ids: [],
    });
    setShowCharacterModal(true);
  };
  
  const handleEditCharacter = (character: Character) => {
    setEditingCharacterId(character.id);
    setNewCharacter({
      name: character.name,
      class: character.class,
      level: character.level,
      health: character.health,
      max_health: character.max_health || 100,
      mana: character.mana || 50,
      max_mana: character.max_mana || 50,
      strength: character.strength || 10,
      dexterity: character.dexterity || 10,
      intelligence: character.intelligence || 10,
      constitution: character.constitution || 10,
      description: character.description || '',
      special_ability: character.special_ability || '',
      damage_formula: character.damage_formula || '5+strength*0.5',
      health_formula: character.health_formula || '50+constitution*5',
      mana_formula: character.mana_formula || '20+intelligence*3',
      movement_formula: character.movement_formula || '3+dexterity*0.1',
      item_ids: character.item_ids || (character.items || []).map((item) => item.id),
      spell_ids: character.spell_ids || (character.spells || []).map((spell) => spell.id),
    });
    setShowCharacterModal(true);
  };
  
  const handleSaveCharacter = async () => {
    if (!newCharacter.name.trim()) {
      alert('Bitte gib einen Namen ein!');
      return;
    }
    
    const maxHealthFromFormula = calculateDamage(newCharacter.health_formula || '50+constitution*5', newCharacter as unknown as Character, '50+constitution*5');
    const maxManaFromFormula = calculateDamage(newCharacter.mana_formula || '20+intelligence*3', newCharacter as unknown as Character, '20+intelligence*3');
    const characterData = {
      name: newCharacter.name,
      class: newCharacter.class,
      level: newCharacter.level,
      health: newCharacter.health,
      max_health: maxHealthFromFormula,
      mana: newCharacter.mana,
      max_mana: maxManaFromFormula,
      strength: newCharacter.strength,
      dexterity: newCharacter.dexterity,
      intelligence: newCharacter.intelligence,
      constitution: newCharacter.constitution,
      description: newCharacter.description,
      special_ability: newCharacter.special_ability,
      damage_formula: newCharacter.damage_formula,
      health_formula: newCharacter.health_formula,
      mana_formula: newCharacter.mana_formula,
      movement_formula: newCharacter.movement_formula,
      item_ids: newCharacter.item_ids,
      spell_ids: newCharacter.spell_ids,
    };
    
    try {
      const response = await fetch(
        editingCharacterId ? `${API_URL}/api/characters/${editingCharacterId}` : `${API_URL}/api/characters`,
        {
          method: editingCharacterId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(characterData),
        },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Fehler beim Speichern');
        return;
      }
      setToastMessage(editingCharacterId ? 'Charakter aktualisiert!' : 'Charakter erstellt!');
      fetchCharacters();
      setShowCharacterModal(false);
      setEditingCharacterId(null);
      setTimeout(() => setToastMessage(null), 3000);
    } catch (error) {
      alert('Fehler beim Speichern');
    }
  };

  // Item Modal Functions
  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setEditingItemId(null);
  };
  
  const handleOpenItemModal = () => {
    setEditingItemId(null);
    setNewItem({
      name: '',
      type: 'Waffe',
      icon: '',
      icon_url: DEFAULT_ITEM_ICON,
      description: '',
      effect: '',
      price: 0,
      essence_price: 0,
      strength_bonus: 0,
      dexterity_bonus: 0,
      intelligence_bonus: 0,
      constitution_bonus: 0,
      health_bonus: 0,
      damage_bonus: 0,
    });
    setShowItemModal(true);
  };
  
  const handleEditItem = async (item: Item) => {
    // Parse icon from name (format: "⚔️ Item Name")
    let icon = '⚔️';
    let name = item.name;
    const iconMatch = item.name.match(/^(\p{Emoji}+)\s+(.+)$/u);
    if (iconMatch) {
      icon = iconMatch[1];
      name = iconMatch[2];
    }
    
    setEditingItemId(item.id);
    setNewItem({
      name: name,
      type: item.type,
      icon: icon,
      icon_url: item.icon_url || DEFAULT_ITEM_ICON,
      description: item.description || '',
      effect: item.effect || '',
      price: item.price ?? 0,
      essence_price: item.essence_price ?? 0,
      strength_bonus: item.strength_bonus || 0,
      dexterity_bonus: item.dexterity_bonus || 0,
      intelligence_bonus: item.intelligence_bonus || 0,
      constitution_bonus: item.constitution_bonus || 0,
      health_bonus: item.health_bonus || 0,
      damage_bonus: item.damage_bonus || 0,
    });
    setShowItemModal(true);
  };

  const handleItemIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/upload/item-icon`, { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Upload fehlgeschlagen');
        return;
      }
      const data = await res.json();
      setNewItem(prev => ({ ...prev, icon_url: data.url || '', icon: '' }));
    } catch {
      alert('Upload fehlgeschlagen');
    }
    e.target.value = '';
  };

  const handleSaveItem = async () => {
    if (!newItem.name.trim()) {
      alert('Bitte gib einen Namen ein!');
      return;
    }
    try {
      const itemData = {
        name: newItem.name.trim(),
        type: newItem.type,
        description: newItem.description,
        effect: newItem.effect,
        price: newItem.price ?? 0,
        essence_price: newItem.essence_price ?? 0,
        strength_bonus: newItem.strength_bonus || 0,
        dexterity_bonus: newItem.dexterity_bonus || 0,
        intelligence_bonus: newItem.intelligence_bonus || 0,
        constitution_bonus: newItem.constitution_bonus || 0,
        health_bonus: newItem.health_bonus || 0,
        damage_bonus: newItem.damage_bonus || 0,
        icon_url: newItem.icon_url || null
      };
      
      if (editingItemId) {
        // Update existing item
        await fetch(`${API_URL}/api/items/${editingItemId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(itemData),
        });
        setToastMessage('Item aktualisiert!');
      } else {
        // Create new item
        await fetch(`${API_URL}/api/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(itemData),
        });
        setToastMessage('Item erstellt!');
      }
      fetchItems();
      handleCloseItemModal();
      setTimeout(() => setToastMessage(null), 3000);
    } catch (error) {
      alert(`Fehler beim ${editingItemId ? 'Aktualisieren' : 'Erstellen'}`);
    }
  };

  // Spell Modal Functions
  const handleOpenSpellModal = () => {
    setEditingSpellId(null);
    setNewSpell({
      name: '',
      type: 'Feuer',
      icon: '',
      icon_url: DEFAULT_SPELL_ICON,
      level: 1,
      description: '',
      effect: '',
      mana_cost: 10
    });
    setShowSpellModal(true);
  };
  
  const handleEditSpell = (spell: Spell) => {
    // Extract icon and name from spell name (format: "🔥 Name" - nur bei Emoji am Anfang trennen)
    let icon = '🔥';
    let name = spell.name;
    const iconMatch = spell.name.match(/^(\p{Emoji}+)\s+(.+)$/u);
    if (iconMatch) {
      icon = iconMatch[1];
      name = iconMatch[2];
    }
    
    // Try to determine type from icon
    let type = 'Feuer';
    if (icon.includes('❄️')) type = 'Eis';
    else if (icon.includes('⚡')) type = 'Blitz';
    else if (icon.includes('🌿')) type = 'Natur';
    else if (icon.includes('💀')) type = 'Dunkel';
    else if (icon.includes('💖') || icon.includes('💗')) type = 'Licht';
    else if (icon.includes('🔮')) type = 'Arkan';
    else if (icon.includes('💗')) type = 'Heilung';
    
    setNewSpell({
      name: name,
      type: type,
      icon: icon,
      icon_url: spell.icon_url || DEFAULT_SPELL_ICON,
      level: spell.level,
      description: spell.description,
      effect: spell.effect,
      mana_cost: spell.mana_cost
    });
    setEditingSpellId(spell.id);
    setShowSpellModal(true);
  };

  const handleSpellIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/upload/spell-icon`, { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Upload fehlgeschlagen');
        return;
      }
      const data = await res.json();
      setNewSpell(prev => ({ ...prev, icon_url: data.url || '', icon: '' }));
    } catch {
      alert('Upload fehlgeschlagen');
    }
    e.target.value = '';
  };
  
  const handleCloseSpellModal = () => {
    setShowSpellModal(false);
    setEditingSpellId(null);
    setNewSpell({
      name: '',
      type: 'Feuer',
      icon: '',
      icon_url: DEFAULT_SPELL_ICON,
      level: 1,
      description: '',
      effect: '',
      mana_cost: 10
    });
  };
  
  const handleAddSpell = async () => {
    if (!newSpell.name.trim()) {
      alert('Bitte gib einen Namen ein!');
      return;
    }
    try {
      const spellData = {
        name: newSpell.name.trim(),
        level: newSpell.level,
        description: newSpell.description,
        effect: newSpell.effect,
        mana_cost: newSpell.mana_cost,
        icon_url: newSpell.icon_url || null
      };
      
      if (editingSpellId) {
        // Update existing spell
        await fetch(`${API_URL}/api/spells/${editingSpellId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(spellData),
        });
        setToastMessage('Zauber aktualisiert!');
      } else {
        // Create new spell
        await fetch(`${API_URL}/api/spells`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(spellData),
        });
        setToastMessage('Zauber erstellt!');
      }
      fetchSpells();
      handleCloseSpellModal();
      setTimeout(() => setToastMessage(null), 3000);
    } catch (error) {
      alert(`Fehler beim ${editingSpellId ? 'Aktualisieren' : 'Erstellen'}`);
    }
  };

  // Character Creation Modal
  const renderCharacterModal = () => (
    <div className="modal-overlay" onClick={() => { setShowCharacterModal(false); setEditingCharacterId(null); }}>
      <div className="modal-content character-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingCharacterId ? '✏️ Charakter bearbeiten' : '⚔️ Neuer Charakter'}</h2>
          <button className="modal-close" onClick={() => { setShowCharacterModal(false); setEditingCharacterId(null); }}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="character-form">
            {/* Basic Info */}
            <div className="form-section">
              <h3>📜 Grunddaten</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newCharacter.name}
                    onChange={e => setNewCharacter({...newCharacter, name: e.target.value})}
                    placeholder="z.B. Thorin der Tapfere"
                  />
                </div>
                <div className="form-group">
                  <label>Klasse</label>
                  {classes.length > 0 ? (
                    <>
                      <select
                        value={classes.some(c => c.name === newCharacter.class) ? newCharacter.class : '__sonstige__'}
                        onChange={e => {
                          const v = e.target.value;
                          setNewCharacter({...newCharacter, class: v === '__sonstige__' ? '' : v});
                        }}
                      >
                        {classes.map(c => (
                          <option key={c.id} value={c.name}>{c.name}</option>
                        ))}
                        <option value="__sonstige__">✏️ Sonstige (eigene eingeben)</option>
                      </select>
                      {(!newCharacter.class || !classes.some(c => c.name === newCharacter.class)) && (
                        <input
                          type="text"
                          value={newCharacter.class}
                          onChange={e => setNewCharacter({...newCharacter, class: e.target.value})}
                          placeholder="z.B. Barde, Druide"
                          style={{ marginTop: 6 }}
                        />
                      )}
                    </>
                  ) : (
                    <input
                      type="text"
                      value={newCharacter.class}
                      onChange={e => setNewCharacter({...newCharacter, class: e.target.value})}
                      placeholder="z.B. Krieger"
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Level</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newCharacter.level}
                    onChange={e => setNewCharacter({...newCharacter, level: parseInt(e.target.value) || 1})}
                  />
                </div>
              </div>
            </div>
            
            {/* Vitals */}
            <div className="form-section">
              <h3>❤️ Vitalwerte</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Leben</label>
                  <input
                    type="number"
                    min="1"
                    value={newCharacter.health}
                    onChange={e => setNewCharacter({...newCharacter, health: parseInt(e.target.value) || 100})}
                  />
                </div>
                <div className="form-group form-group-readonly">
                  <label>Max. Leben (aus Formel)</label>
                  <span className="form-value-readonly">
                    {calculateDamage(newCharacter.health_formula || '50+constitution*5', newCharacter as unknown as Character, '50+constitution*5')}
                  </span>
                </div>
                <div className="form-group">
                  <label>Mana</label>
                  <input
                    type="number"
                    min="0"
                    value={newCharacter.mana}
                    onChange={e => setNewCharacter({...newCharacter, mana: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="form-group form-group-readonly">
                  <label>Max. Mana (aus Formel)</label>
                  <span className="form-value-readonly">
                    {calculateDamage(newCharacter.mana_formula || '20+intelligence*3', newCharacter as unknown as Character, '20+intelligence*3')}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Base Stats */}
            <div className="form-section">
              <h3>📊 Basis Attribute</h3>
              <div className="stats-form-grid">
                <div className="stat-form-item">
                  <span className="stat-icon">💪</span>
                  <label>Staerke</label>
                  <div className="stat-input-controls">
                    <button 
                      type="button"
                      className="stat-adjust-btn"
                      onClick={() => setNewCharacter({...newCharacter, strength: Math.max(1, newCharacter.strength - 1)})}
                    >-</button>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={newCharacter.strength}
                      onChange={e => setNewCharacter({...newCharacter, strength: parseInt(e.target.value) || 10})}
                    />
                    <button 
                      type="button"
                      className="stat-adjust-btn"
                      onClick={() => setNewCharacter({...newCharacter, strength: Math.min(100, newCharacter.strength + 1)})}
                    >+</button>
                  </div>
                </div>
                
                <div className="stat-form-item">
                  <span className="stat-icon">🎯</span>
                  <label>Geschick</label>
                  <div className="stat-input-controls">
                    <button 
                      type="button"
                      className="stat-adjust-btn"
                      onClick={() => setNewCharacter({...newCharacter, dexterity: Math.max(1, newCharacter.dexterity - 1)})}
                    >-</button>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={newCharacter.dexterity}
                      onChange={e => setNewCharacter({...newCharacter, dexterity: parseInt(e.target.value) || 10})}
                    />
                    <button 
                      type="button"
                      className="stat-adjust-btn"
                      onClick={() => setNewCharacter({...newCharacter, dexterity: Math.min(100, newCharacter.dexterity + 1)})}
                    >+</button>
                  </div>
                </div>
                
                <div className="stat-form-item">
                  <span className="stat-icon">🧠</span>
                  <label>Intelligenz</label>
                  <div className="stat-input-controls">
                    <button 
                      type="button"
                      className="stat-adjust-btn"
                      onClick={() => setNewCharacter({...newCharacter, intelligence: Math.max(1, newCharacter.intelligence - 1)})}
                    >-</button>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={newCharacter.intelligence}
                      onChange={e => setNewCharacter({...newCharacter, intelligence: parseInt(e.target.value) || 10})}
                    />
                    <button 
                      type="button"
                      className="stat-adjust-btn"
                      onClick={() => setNewCharacter({...newCharacter, intelligence: Math.min(100, newCharacter.intelligence + 1)})}
                    >+</button>
                  </div>
                </div>
                
                <div className="stat-form-item">
                  <span className="stat-icon">❤️</span>
                  <label>Konstitution</label>
                  <div className="stat-input-controls">
                    <button 
                      type="button"
                      className="stat-adjust-btn"
                      onClick={() => setNewCharacter({...newCharacter, constitution: Math.max(1, newCharacter.constitution - 1)})}
                    >-</button>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={newCharacter.constitution}
                      onChange={e => setNewCharacter({...newCharacter, constitution: parseInt(e.target.value) || 10})}
                    />
                    <button 
                      type="button"
                      className="stat-adjust-btn"
                      onClick={() => setNewCharacter({...newCharacter, constitution: Math.min(100, newCharacter.constitution + 1)})}
                    >+</button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Description & Special Ability */}
            <div className="form-section">
              <h3>📖 Details</h3>
              <div className="form-group full-width">
                <label>Beschreibung</label>
                <textarea
                  value={newCharacter.description}
                  onChange={e => setNewCharacter({...newCharacter, description: e.target.value})}
                  placeholder="Eine kurze Hintergrundgeschichte des Charakters..."
                  rows={3}
                />
              </div>
              <div className="form-group full-width">
                <label>Spezialfaehigkeit</label>
                <textarea
                  value={newCharacter.special_ability}
                  onChange={e => setNewCharacter({...newCharacter, special_ability: e.target.value})}
                  placeholder="z.B. Kann einmal pro Kampf doppelten Schaden verursachen..."
                  rows={2}
                />
              </div>
              <div className="form-group full-width">
                <label>⚔️ Schadensformel</label>
                <input
                  type="text"
                  value={newCharacter.damage_formula}
                  onChange={e => setNewCharacter({...newCharacter, damage_formula: e.target.value})}
                  placeholder="z.B. 5+strength*0.5+dexterity*0.2"
                />
              </div>
              <div className="form-group full-width">
                <label>❤️ Lebensformel (Max-Health)</label>
                <input
                  type="text"
                  value={newCharacter.health_formula}
                  onChange={e => setNewCharacter({...newCharacter, health_formula: e.target.value})}
                  placeholder="z.B. 50+constitution*5"
                />
              </div>
              <div className="form-group full-width">
                <label>✨ Manaformel (Max-Mana)</label>
                <input
                  type="text"
                  value={newCharacter.mana_formula}
                  onChange={e => setNewCharacter({...newCharacter, mana_formula: e.target.value})}
                  placeholder="z.B. 20+intelligence*3"
                />
              </div>
              <div className="form-group full-width">
                <label>🚶 Bewegungspunkte-Formel</label>
                <input
                  type="text"
                  value={newCharacter.movement_formula}
                  onChange={e => setNewCharacter({...newCharacter, movement_formula: e.target.value})}
                  placeholder="z.B. min(18, max(6, dexterity - 8))"
                />
                <p className="form-help-text">
                  Ergebnis: {calculateDamage(newCharacter.movement_formula || '3+dexterity*0.1', newCharacter as unknown as Character, '3+dexterity*0.1')} Bewegung
                </p>
              </div>
              <div className="formula-help">
                <small>
                  Verfuegbare Variablen: <code>strength</code>, <code>dexterity</code>, <code>intelligence</code>, <code>constitution</code>, <code>level</code>
                  <br/>Funktionen: <code>min</code>, <code>max</code>
                  <br/>Beispiele: <code>5+strength*0.5</code> | <code>50+constitution*5</code> | <code>min(18, max(6, dexterity - 8))</code>
                </small>
              </div>
            </div>

            <div className="form-section">
              <h3>🎒 Startausrüstung</h3>
              <p className="form-help-text">
                Diese Items und Zauber hat der Charakter, sobald ein Spieler ihn wählt.
                Tränke, Gold und Naturessenz dürfen mehrfach dazu, andere Ausrüstung nur einmal.
              </p>
              <div className="form-group full-width">
                <label htmlFor="start-item">Items</label>
                <div className="kit-picker-selected">
                  {newCharacter.item_ids.length === 0 && <span className="form-help-text">Keine Start-Items</span>}
                  {newCharacter.item_ids.map((id, index) => {
                    const item = items.find((entry) => entry.id === id);
                    return (
                      <button
                        type="button"
                        key={`${id}-${index}`}
                        className="kit-picker-chip"
                        onClick={() => setNewCharacter({
                          ...newCharacter,
                          item_ids: newCharacter.item_ids.filter((_, chipIndex) => chipIndex !== index),
                        })}
                      >
                        {item?.name || `Item ${id}`} ×
                      </button>
                    );
                  })}
                </div>
                <select
                  id="start-item"
                  value=""
                  onChange={(event) => {
                    const id = parseInt(event.target.value, 10);
                    if (!id) return;
                    const item = items.find((entry) => entry.id === id);
                    if (item && !isStackableItem(item) && newCharacter.item_ids.includes(id)) return;
                    setNewCharacter({ ...newCharacter, item_ids: [...newCharacter.item_ids, id] });
                  }}
                >
                  <option value="">Item hinzufügen…</option>
                  {items
                    .filter((item) => isStackableItem(item) || !newCharacter.item_ids.includes(item.id))
                    .map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                </select>
              </div>
              <div className="form-group full-width">
                <label htmlFor="start-spell">Zauber</label>
                <div className="kit-picker-selected">
                  {newCharacter.spell_ids.length === 0 && <span className="form-help-text">Keine Start-Zauber</span>}
                  {newCharacter.spell_ids.map((id) => {
                    const spell = spells.find((entry) => entry.id === id);
                    return (
                      <button
                        type="button"
                        key={id}
                        className="kit-picker-chip"
                        onClick={() => setNewCharacter({
                          ...newCharacter,
                          spell_ids: newCharacter.spell_ids.filter((spellId) => spellId !== id),
                        })}
                      >
                        {spell?.name || `Zauber ${id}`} ×
                      </button>
                    );
                  })}
                </div>
                <select
                  id="start-spell"
                  value=""
                  onChange={(event) => {
                    const id = parseInt(event.target.value, 10);
                    if (!id || newCharacter.spell_ids.includes(id)) return;
                    setNewCharacter({ ...newCharacter, spell_ids: [...newCharacter.spell_ids, id] });
                  }}
                >
                  <option value="">Zauber hinzufügen…</option>
                  {spells
                    .filter((spell) => !newCharacter.spell_ids.includes(spell.id))
                    .map((spell) => (
                      <option key={spell.id} value={spell.id}>{spell.name}</option>
                    ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="medieval-button cancel-btn" onClick={() => setShowCharacterModal(false)}>
            Abbrechen
          </button>
          <button className="medieval-button create-btn" onClick={handleSaveCharacter}>
            {editingCharacterId ? '💾 Speichern' : '⚔️ Charakter erstellen'}
          </button>
        </div>
      </div>
    </div>
  );

  // Item Creation Modal
  const renderItemModal = () => (
    <div className="modal-overlay" onClick={handleCloseItemModal}>
      <div className="modal-content item-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingItemId ? '✏️ Item bearbeiten' : '🗡️ Neues Item'}</h2>
          <button className="modal-close" onClick={handleCloseItemModal}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="item-form">
            {/* Icon Selection */}
            <div className="form-section">
              <h3>Wappen wählen</h3>
              <PictogramPicker
                kind="item"
                value={isKitIcon(newItem.icon_url) ? newItem.icon_url : ''}
                onChange={icon => setNewItem({
                  ...newItem,
                  icon_url: icon.src,
                  icon: '',
                  type: icon.itemType || newItem.type,
                })}
              />
              <div className="form-group" style={{ marginTop: 10 }}>
                <label>Eigenes Bild hochladen</label>
                <div style={{ marginBottom: '10px' }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={handleItemIconUpload}
                    id="item-icon-upload"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="item-icon-upload" className="medieval-button" style={{ display: 'inline-block', cursor: 'pointer', padding: '8px 16px' }}>
                    Datei auswählen
                  </label>
                  <span style={{ marginLeft: '10px', color: '#ccc' }}>
                    {newItem.icon_url && !isKitIcon(newItem.icon_url) ? 'Bild hochgeladen ✓' : 'Keine Datei ausgewählt'}
                  </span>
                </div>
                {newItem.icon_url && !isKitIcon(newItem.icon_url) && (
                  <div className="custom-icon-preview" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img
                      src={mediaSrc(newItem.icon_url)}
                      alt="Icon Vorschau"
                      style={{ maxWidth: '64px', maxHeight: '64px', border: '1px solid #666', borderRadius: '4px' }}
                    />
                    <button
                      type="button"
                      className="medieval-button small-button"
                      onClick={() => setNewItem({...newItem, icon_url: DEFAULT_ITEM_ICON, icon: ''})}
                    >
                      Entfernen
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Basic Info */}
            <div className="form-section">
              <h3>📜 Grunddaten</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newItem.name}
                    onChange={e => setNewItem({...newItem, name: e.target.value})}
                    placeholder="z.B. Flammenschwert"
                  />
                </div>
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={newItem.type}
                    onChange={e => setNewItem({...newItem, type: e.target.value})}
                  >
                    {ITEM_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>💰 Gold</label>
                  <input
                    type="number"
                    value={newItem.price}
                    onChange={e => setNewItem({...newItem, price: parseInt(e.target.value) || 0})}
                    min="0"
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>🍃 Naturessenz</label>
                  <input
                    type="number"
                    value={newItem.essence_price}
                    onChange={e => setNewItem({...newItem, essence_price: parseInt(e.target.value) || 0})}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
            
            {/* Description & Effect */}
            <div className="form-section">
              <h3>📖 Details</h3>
              <div className="form-group full-width">
                <label>Beschreibung</label>
                <textarea
                  value={newItem.description}
                  onChange={e => setNewItem({...newItem, description: e.target.value})}
                  placeholder="Eine mysteriöse Waffe aus vergangenen Zeiten..."
                  rows={2}
                />
              </div>
              <div className="form-group full-width">
                <label>Effekt</label>
                <textarea
                  value={newItem.effect}
                  onChange={e => setNewItem({...newItem, effect: e.target.value})}
                  placeholder="z.B. +4 Schaden, +3 Reichweite"
                  rows={2}
                />
                <ItemEffectModifiers
                  effect={newItem.effect}
                  onEffectChange={next => setNewItem({ ...newItem, effect: next })}
                />
              </div>
            </div>
            
            {/* Stat Bonuses */}
            <div className="form-section">
              <h3>📊 Stat-Verbesserungen</h3>
              <p className="form-help-text">Optionale Zahlenfelder. Stärke und Schaden zählen zum Schaden. Leben ist flaches Max-Leben und kommt zur Konstitution dazu, ohne sie noch einmal umzurechnen.</p>
              <div className="stats-bonus-grid">
                {ITEM_STAT_MODIFIERS.map(stat => (
                  <div className="form-group" key={stat.key}>
                    <label htmlFor={`item-stat-${stat.key}`}>{stat.icon} {stat.label} <span className="stat-field-hint">{stat.hint}</span></label>
                    <input
                      id={`item-stat-${stat.key}`}
                      type="number"
                      value={newItem[stat.key]}
                      onChange={e => setNewItem({ ...newItem, [stat.key]: parseInt(e.target.value, 10) || 0 })}
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Preview */}
            <div className="form-section preview-section">
              <h3>👁️ Vorschau</h3>
              <div className="item-preview">
                <Pictogram iconUrl={newItem.icon_url} type={newItem.type} name={newItem.name} kind="item" className="preview-icon-img" />
                <span className="preview-name">{newItem.name || 'Item Name'}</span>
                <span className="preview-type">{newItem.type}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="medieval-button cancel-btn" onClick={handleCloseItemModal}>
            Abbrechen
          </button>
          <button className="medieval-button create-btn" onClick={handleSaveItem}>
            {editingItemId ? '💾 Speichern' : '🗡️ Item erstellen'}
          </button>
        </div>
      </div>
    </div>
  );

  // Spell Creation Modal
  const renderSpellModal = () => (
    <div className="modal-overlay" onClick={handleCloseSpellModal}>
      <div className="modal-content spell-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingSpellId ? '✏️ Zauber bearbeiten' : '✨ Neuer Zauber'}</h2>
          <button className="modal-close" onClick={handleCloseSpellModal}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="spell-form">
            {/* Icon Selection */}
            <div className="form-section">
              <h3>Wappen wählen</h3>
              <PictogramPicker
                kind="spell"
                value={isKitIcon(newSpell.icon_url) ? newSpell.icon_url : ''}
                onChange={icon => setNewSpell({
                  ...newSpell,
                  icon_url: icon.src,
                  icon: '',
                  type: icon.category,
                })}
              />
              <div className="form-group" style={{ marginTop: 10 }}>
                <label>Eigenes Bild hochladen</label>
                <div style={{ marginBottom: '10px' }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={handleSpellIconUpload}
                    id="spell-icon-upload"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="spell-icon-upload" className="medieval-button" style={{ display: 'inline-block', cursor: 'pointer', padding: '8px 16px' }}>
                    Datei auswählen
                  </label>
                  <span style={{ marginLeft: '10px', color: '#ccc' }}>
                    {newSpell.icon_url && !isKitIcon(newSpell.icon_url) ? 'Bild hochgeladen ✓' : 'Keine Datei ausgewählt'}
                  </span>
                </div>
                {newSpell.icon_url && !isKitIcon(newSpell.icon_url) && (
                  <div className="custom-icon-preview" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img
                      src={mediaSrc(newSpell.icon_url)}
                      alt="Icon Vorschau"
                      style={{ maxWidth: '64px', maxHeight: '64px', border: '1px solid #666', borderRadius: '4px' }}
                    />
                    <button
                      type="button"
                      className="medieval-button small-button"
                      onClick={() => setNewSpell({...newSpell, icon_url: DEFAULT_SPELL_ICON, icon: ''})}
                    >
                      Entfernen
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Basic Info */}
            <div className="form-section">
              <h3>📜 Grunddaten</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newSpell.name}
                    onChange={e => setNewSpell({...newSpell, name: e.target.value})}
                    placeholder="z.B. Feuerball"
                  />
                </div>
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={newSpell.type}
                    onChange={e => setNewSpell({...newSpell, type: e.target.value})}
                  >
                    {SPELL_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Level</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={newSpell.level}
                    onChange={e => setNewSpell({...newSpell, level: parseInt(e.target.value) || 1})}
                  />
                </div>
                <div className="form-group">
                  <label>Manakosten</label>
                  <input
                    type="number"
                    min="0"
                    value={newSpell.mana_cost}
                    onChange={e => setNewSpell({...newSpell, mana_cost: parseInt(e.target.value) || 10})}
                  />
                </div>
              </div>
            </div>
            
            {/* Description & Effect */}
            <div className="form-section">
              <h3>📖 Details</h3>
              <div className="form-group full-width">
                <label>Beschreibung</label>
                <textarea
                  value={newSpell.description}
                  onChange={e => setNewSpell({...newSpell, description: e.target.value})}
                  placeholder="Ein mächtiger Feuerzauber der Zerstörung..."
                  rows={2}
                />
              </div>
              <div className="form-group full-width">
                <label>Effekt *</label>
                <textarea
                  value={newSpell.effect}
                  onChange={e => setNewSpell({...newSpell, effect: e.target.value})}
                  placeholder="z.B. Macht {5+2*intelligence} Schaden oder Verursacht {3+level} Feuer Schaden"
                  rows={3}
                />
                <p className="form-help-text">
                  💡 <strong>Formeln verwenden:</strong> Schreibe Formeln in geschweifte Klammern <code>{'{formel}'}</code>
                  und Zielgruppen mit <code>N Ziel(e):</code>. Klauseln trennt ein Strichpunkt.<br/>
                  <strong>Verfügbare Variablen:</strong> strength, dexterity, intelligence, constitution, level, basisschaden, schaden<br/>
                  <code>basisschaden</code> ist nur die Schadensformel. <code>schaden</code> ist diese Formel plus Waffen- und Buffbonus.<br/>
                  <strong>Beispiele:</strong><br/>
                  • <code>1 Ziel: 6 Schaden, Eingefroren</code> — ein Gegner, Status bis der Admin ihn aufhebt<br/>
                  • <code>3 Ziele: 4 Schaden</code> — drei gewählte Ziele<br/>
                  • <code>1 Gegner: 4 Schaden; 3 Ziele: -2 Bewegung</code> — Treffer plus Umkreis<br/>
                  • <code>1 Ziel: 8 Schaden, Brennen 3</code> — Feuer, Brandschaden löst der Admin aus<br/>
                  • <code>Macht {'{5+2*intelligence}'} Schaden</code> — klassische Schadensformel
                </p>
                <div className="armor-formula-examples">
                  {[
                    '1 Ziel: 6 Schaden, Eingefroren',
                    '3 Ziele: 4 Schaden',
                    '1 Gegner: 4 Schaden; 3 Ziele: -2 Bewegung',
                    '1 Ziel: 8 Schaden, Brennen 3',
                  ].map((formula) => (
                    <button
                      key={formula}
                      type="button"
                      className="medieval-button cancel-btn"
                      onClick={() => setNewSpell({ ...newSpell, effect: formula })}
                    >
                      {formula}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Preview */}
            <div className="form-section preview-section">
              <h3>👁️ Vorschau</h3>
              <div className="spell-preview">
                <Pictogram iconUrl={newSpell.icon_url} name={newSpell.name} kind="spell" className="preview-icon-img" />
                <span className="preview-name">{newSpell.name || 'Zauber Name'}</span>
                <span className="preview-stats">Lvl {newSpell.level} • {newSpell.mana_cost} Mana</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="medieval-button cancel-btn" onClick={handleCloseSpellModal}>
            Abbrechen
          </button>
          <button className="medieval-button create-btn" onClick={handleAddSpell}>
            {editingSpellId ? '💾 Speichern' : '✨ Zauber erstellen'}
          </button>
        </div>
      </div>
    </div>
  );

  const appValue: AppContextValue = {
    currentPage, setCurrentPage, adminTab, setAdminTab, isAuthenticated, setIsAuthenticated,
    showRules, setShowRules, username, setUsername, password, setPassword,
    games, setGames, players, setPlayers, characters, setCharacters, classes, setClasses, items, setItems, spells, setSpells, rules, setRules,
    currentGameId, setCurrentGameId, gameOverview, setGameOverview, overviewLoadError, setOverviewLoadError, toastMessage, setToastMessage,
    playerName, setPlayerName, playerId, setPlayerId, activeGames, setActiveGames, pendingRequest, setPendingRequest,
    selectedGameForChar, setSelectedGameForChar, selectedCharacterId, setSelectedCharacterId,
    availableCharacters, setAvailableCharacters, characterSheet, setCharacterSheet, statDistribution, setStatDistribution,
    showCharacterModal, setShowCharacterModal, editingCharacterId, setEditingCharacterId, newCharacter, setNewCharacter,
    activeFormulaPopup, setActiveFormulaPopup, showItemModal, setShowItemModal, editingItemId, setEditingItemId, newItem, setNewItem,
    showSpellModal, setShowSpellModal, editingSpellId, setEditingSpellId, newSpell, setNewSpell,
    hoveredSpellId, setHoveredSpellId, showSpellFormulaPopup, setShowSpellFormulaPopup,
    editingRuleId, setEditingRuleId, isAddingRule, setIsAddingRule, newRule, setNewRule,
    editingClassId, setEditingClassId, isAddingClass, setIsAddingClass, newClass, setNewClass,
    handleFormulaToggle, handleAdminLogin, handleLogout, fetchGames, fetchPlayers, fetchCharacters, fetchClasses, fetchItems, fetchSpells, fetchRules,
    handleDeleteGame, handleDeletePlayer, handleDeleteCharacter, handleDeleteItem,
    handleAddRule, handleEditRule, handleUpdateRule, handleDeleteRule, handleCancelRuleEdit,
    handleAddClass, handleEditClass, handleUpdateClass, handleDeleteClass, handleCancelClassEdit,
    handleDeleteSpell,
    handleCreateGame, fetchGameOverview, handleAcceptJoinRequest, handleRejectJoinRequest,
    handleLevelUp, handleBulkLevelUp, handleResetLevel, handleSetLevel, handleAssignItem, handleUseCharacterItem, handleRemoveItem,
    handleAssignSpell, handleRemoveSpell, handleSetHealthMana,
    handlePlayerNameSubmit, handlePlayerGroupSubmit, addCompanion, fetchActiveGames, handleJoinGameRequest, handleCancelRequest, handlePlayerLogout,
    fetchAvailableCharacters, handleSelectCharacter, fetchCharacterSheet, handleStatIncrease, handleStatDecrease,
    evaluateFormulasInText, extractFormulasFromText, calculateDamage, handleCommitLevelUp, handleUseItem,
    handleAddPlayer, handleOpenCharacterModal, handleEditCharacter, handleSaveCharacter,
    handleCloseItemModal, handleOpenItemModal, handleEditItem, handleSaveItem,
    handleOpenSpellModal, handleCloseSpellModal, handleEditSpell, handleAddSpell,
  };

  return (
    <AppContext.Provider value={appValue}>
      <div className="App">
        <AppRoutes />

        {showCharacterModal && renderCharacterModal()}
        {showItemModal && renderItemModal()}
        {showSpellModal && renderSpellModal()}

        <Toast message={toastMessage} />
      </div>
    </AppContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;

